import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';

const CreateOrganizationModal = ({ isOpen, onClose, onCreateOrganization }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    description: '',
    website: '',
    industry: '',
    size: '',
    organization_category: '',
    
    // Contact Information
    contact_email: '',
    contact_phone: '',
    
    // Address Details
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    
    // Regional Settings
    timezone: 'UTC',
    language: 'en',
    allowed_domains: ''
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Options for dropdowns
  const industryOptions = [
    { value: 'technology', label: 'Technology' },
    { value: 'finance', label: 'Finance' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'education', label: 'Education' },
    { value: 'retail', label: 'Retail' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'nonprofit', label: 'Non-profit' },
    { value: 'government', label: 'Government' },
    { value: 'other', label: 'Other' }
  ];

  const sizeOptions = [
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-500', label: '201-500 employees' },
    { value: '501-1000', label: '501-1000 employees' },
    { value: '1000+', label: '1000+ employees' }
  ];

  const categoryOptions = [
    { value: 'startup', label: 'Startup' },
    { value: 'small_business', label: 'Small Business' },
    { value: 'enterprise', label: 'Enterprise' },
    { value: 'agency', label: 'Agency' },
    { value: 'freelancer', label: 'Freelancer' },
    { value: 'nonprofit', label: 'Non-profit' },
    { value: 'government', label: 'Government' },
    { value: 'other', label: 'Other' }
  ];

  const timezoneOptions = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
    { value: 'Europe/Berlin', label: 'Central European Time (CET)' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
    { value: 'Asia/Shanghai', label: 'China Standard Time (CST)' },
    { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'it', label: 'Italian' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'ja', label: 'Japanese' },
    { value: 'zh', label: 'Chinese' },
    { value: 'ko', label: 'Korean' },
    { value: 'ar', label: 'Arabic' }
  ];

  const countryOptions = [
    { value: 'US', label: 'United States' },
    { value: 'CA', label: 'Canada' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' },
    { value: 'IT', label: 'Italy' },
    { value: 'ES', label: 'Spain' },
    { value: 'AU', label: 'Australia' },
    { value: 'JP', label: 'Japan' },
    { value: 'CN', label: 'China' },
    { value: 'IN', label: 'India' },
    { value: 'BR', label: 'Brazil' },
    { value: 'MX', label: 'Mexico' },
    { value: 'OTHER', label: 'Other' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, logo: 'Please select an image file' }));
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, logo: 'File size must be less than 5MB' }));
        return;
      }
      
      setLogoFile(file);
      setErrors(prev => ({ ...prev, logo: '' }));
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Organization name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Organization name must be at least 2 characters long';
    }
    
    if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    // Step 2 validation is optional for most fields
    return true;
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.error('🖱️ Create Organization button clicked! Current Step:', currentStep);
    
    if (currentStep === 1) {
      console.error('👉 Step 1: Proceeding to next step');
      handleNextStep();
      return;
    }
    
    console.error('🛡️ Validating Step 2');
    const isValid = validateStep2();
    console.error('🛡️ Step 2 Valid?', isValid);

    if (!isValid) {
      console.error('❌ Step 2 Invalid');
      return;
    }

    console.error('⏳ Preparing to submit...');
    setIsLoading(true);
    setErrors({});

    try {
      console.error('📞 Calling onCreateOrganization prop...');
      // Prepare form data for submission
      const organizationData = {
        ...formData,
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        website: formData.website.trim() || null,
        contact_email: formData.contact_email.trim() || null,
        contact_phone: formData.contact_phone.trim() || null,
        address_line1: formData.address_line1.trim() || null,
        address_line2: formData.address_line2.trim() || null,
        city: formData.city.trim() || null,
        state: formData.state.trim() || null,
        postal_code: formData.postal_code.trim() || null,
        allowed_domains: formData.allowed_domains.trim() ? 
          formData.allowed_domains.split(',').map(d => d.trim()).filter(d => d) : null
      };

      await onCreateOrganization(organizationData, logoFile);
      
      // Reset form and close modal
      handleClose();
    } catch (err) {
      console.error('❌ Organization creation failed:', err);
      // Show full error message from backend if available
      setErrors({ 
        submit: err.message || 'Failed to create organization. Please check your network connection and try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setCurrentStep(1);
      setFormData({
        name: '', description: '', website: '', industry: '', size: '', organization_category: '',
        contact_email: '', contact_phone: '', address_line1: '', address_line2: '',
        city: '', state: '', postal_code: '', country: '', timezone: 'UTC', language: 'en', allowed_domains: ''
      });
      setLogoFile(null);
      setLogoPreview(null);
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[1050] p-4 animate-in fade-in duration-200'>
      <div className='bg-card rounded-xl shadow-2xl border border-border/50 max-w-2xl w-full max-h-[80vh] flex flex-col my-auto shadow-enterprise'>
        {/* Header - Fixed */}
        <div className='flex items-center justify-between px-6 py-5 border-b border-border/60 flex-shrink-0 bg-card/50 backdrop-blur-sm rounded-t-xl'>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center ring-1 ring-primary/20">
              <Icon name="Building2" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground tracking-tight">Create Organization</h2>
              <p className="text-sm text-muted-foreground">
                Step {currentStep} of 2: <span className="font-medium text-foreground">{currentStep === 1 ? 'Basic Details' : 'Configuration'}</span>
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            disabled={isLoading}
            className="rounded-full hover:bg-destructive/10 hover:text-destructive transition-colors h-9 w-9"
          >
            <Icon name="X" size={18} />
          </Button>
        </div>

        {/* Progress Indicator - Fixed */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-border/60 bg-muted/20">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center space-x-2 ${currentStep >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shadow-sm transition-all duration-300 ${
                currentStep >= 1 ? 'bg-primary text-primary-foreground ring-2 ring-primary/20 scale-110' : 'bg-muted text-muted-foreground border border-border'
              }`}>
                1
              </div>
              <span className={`text-sm font-medium transition-colors ${currentStep >= 1 ? 'text-primary' : ''}`}>Identity</span>
            </div>
            <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div className={`h-full bg-primary transition-all duration-500 ease-out rounded-full ${currentStep >= 2 ? 'w-full' : 'w-1/2'}`} />
            </div>
            <div className={`flex items-center space-x-2 ${currentStep >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shadow-sm transition-all duration-300 ${
                currentStep >= 2 ? 'bg-primary text-primary-foreground ring-2 ring-primary/20 scale-110' : 'bg-muted text-muted-foreground border border-border'
              }`}>
                2
              </div>
              <span className={`text-sm font-medium transition-colors ${currentStep >= 2 ? 'text-primary' : ''}`}>Settings</span>
            </div>
          </div>
        </div>

        {/* Form - Flexible Container */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6 custom-scrollbar scroll-smooth">
            {/* General Error */}
            {errors.submit && (
              <div className="mb-6 bg-destructive/5 border border-destructive/20 rounded-xl p-4 flex items-start space-x-3 shadow-sm animate-in fade-in slide-in-from-top-2">
                <div className="p-1.5 bg-destructive/10 rounded-full flex-shrink-0">
                  <Icon name="AlertCircle" size={16} className="text-destructive" />
                </div>
                <div className="flex-1 pt-0.5">
                    <p className="text-sm font-semibold text-destructive">Submission Failed</p>
                    <p className="text-sm text-destructive/80 mt-1">{errors.submit}</p>
                </div>
              </div>
            )}

            {currentStep === 1 && (
              <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
                {/* Organization Identity Section */}
                <div className="space-y-5">
                  <div className="flex items-center space-x-2 text-primary pb-3 border-b border-border/60">
                    <Icon name="Building2" size={18} className="text-primary/80" />
                    <h3 className="text-base font-semibold text-foreground">Organization Identity</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="md:col-span-2">
                      <Input
                        label="Organization Name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="e.g. Acme Corp"
                        error={errors.name}
                        required
                        disabled={isLoading}
                        className="transition-all focus:ring-2 focus:ring-primary/20 h-10"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-foreground mb-1.5 ml-0.5">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder="What is your organization about?"
                        rows={2}
                        disabled={isLoading}
                        className="w-full px-3 py-2.5 border border-input rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none shadow-sm min-h-[80px]"
                      />
                    </div>
                    
                    <Input
                      label="Website"
                      type="url"
                      value={formData.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      placeholder="https://"
                      disabled={isLoading}
                      icon="Globe"
                    />
                    
                    <Select
                      label="Category"
                      options={categoryOptions}
                      value={formData.organization_category}
                      onChange={(value) => handleInputChange('organization_category', value)}
                      placeholder="Select category"
                      disabled={isLoading}
                    />
                    
                    <Select
                      label="Industry"
                      options={industryOptions}
                      value={formData.industry}
                      onChange={(value) => handleInputChange('industry', value)}
                      placeholder="Select industry"
                      disabled={isLoading}
                    />
                    
                    <Select
                      label="Team Size"
                      options={sizeOptions}
                      value={formData.size}
                      onChange={(value) => handleInputChange('size', value)}
                      placeholder="Select size"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Logo Upload Section */}
                <div className="space-y-5">
                  <div className="flex items-center space-x-2 text-primary pb-3 border-b border-border/60">
                    <Icon name="Image" size={18} className="text-primary/80" />
                    <h3 className="text-base font-semibold text-foreground">Branding</h3>
                  </div>
                  
                  <div className="bg-muted/20 rounded-xl p-5 border border-dashed border-border hover:border-primary/50 transition-colors group">
                      <div className="flex items-center space-x-5">
                        <div className="w-16 h-16 rounded-xl border border-border/60 overflow-hidden bg-card shadow-sm flex items-center justify-center flex-shrink-0 relative group-hover:shadow-md transition-all">
                            {logoPreview ? (
                            <>
                                <img src={logoPreview} alt="Logo preview" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <Icon name="Edit2" size={14} className="text-white" />
                                </div>
                            </>
                            ) : (
                            <Icon name="Building2" size={24} className="text-muted-foreground/40" />
                            )}
                        </div>
                        <div className="flex-1">
                            <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                            id="logo-upload"
                            disabled={isLoading}
                            />
                            <div className="flex items-center space-x-3 mb-2">
                              <Button 
                                type="button" 
                                variant="outline" 
                                disabled={isLoading} 
                                size="sm" 
                                className="h-8 font-medium cursor-pointer"
                                onClick={() => document.getElementById('logo-upload').click()}
                              >
                                <Icon name="Upload" size={14} className="mr-2" />
                                Upload Logo
                              </Button>
                              {logoPreview && (
                                <Button type="button" variant="ghost" size="sm" onClick={() => { setLogoPreview(null); setFormData(prev => ({ ...prev, logo: null })); }} className="h-8 text-destructive hover:text-destructive hover:bg-destructive/10">
                                  <span className="text-xs">Remove</span>
                                </Button>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                              <span className="bg-muted px-1.5 py-0.5 rounded text-[10px] font-medium uppercase">Recommended</span>
                              <span>200x200px • Max 5MB • PNG/JPG</span>
                            </div>
                            {errors.logo && (
                            <p className="text-xs font-semibold text-destructive mt-1.5 animate-pulse flex items-center">
                              <Icon name="AlertCircle" size={12} className="mr-1" />
                              {errors.logo}
                            </p>
                            )}
                        </div>
                      </div>
                  </div>
                </div>

                {/* Contact Information Section */}
                <div className="space-y-5">
                  <div className="flex items-center space-x-2 text-primary pb-3 border-b border-border/60">
                    <Icon name="Mail" size={18} className="text-primary/80" />
                    <h3 className="text-base font-semibold text-foreground">Contact Info</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Input
                      label="Contact Email"
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => handleInputChange('contact_email', e.target.value)}
                      placeholder="admin@company.com"
                      error={errors.contact_email}
                      disabled={isLoading}
                      icon="Mail"
                    />
                    
                    <Input
                      label="Phone Number"
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                      placeholder="+1 (555) 000-0000"
                      disabled={isLoading}
                      icon="Phone"
                    />
                  </div>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
                {/* Address Information Section */}
                <div className="space-y-5">
                  <div className="flex items-center space-x-2 text-primary pb-3 border-b border-border/60">
                    <Icon name="MapPin" size={18} className="text-primary/80" />
                    <h3 className="text-base font-semibold text-foreground">Address</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="md:col-span-2">
                      <Input
                        label="Street Address"
                        type="text"
                        value={formData.address_line1}
                        onChange={(e) => handleInputChange('address_line1', e.target.value)}
                        placeholder="123 Main St"
                        disabled={isLoading}
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <Input
                        label="Apartment / Suite"
                        type="text"
                        value={formData.address_line2}
                        onChange={(e) => handleInputChange('address_line2', e.target.value)}
                        placeholder="Unit 4B (Optional)"
                        disabled={isLoading}
                      />
                    </div>
                    
                    <Input
                      label="City"
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder="New York"
                      disabled={isLoading}
                    />
                    
                    <Input
                      label="State / Province"
                      type="text"
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      placeholder="NY"
                      disabled={isLoading}
                    />
                    
                    <Input
                      label="Postal Code"
                      type="text"
                      value={formData.postal_code}
                      onChange={(e) => handleInputChange('postal_code', e.target.value)}
                      placeholder="10001"
                      disabled={isLoading}
                    />
                    
                    <Select
                      label="Country"
                      options={countryOptions}
                      value={formData.country}
                      onChange={(value) => handleInputChange('country', value)}
                      placeholder="Select country"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Regional Settings Section */}
                <div className="space-y-5">
                  <div className="flex items-center space-x-2 text-primary pb-3 border-b border-border/60">
                    <Icon name="Globe" size={18} className="text-primary/80" />
                    <h3 className="text-base font-semibold text-foreground">Localization</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Select
                      label="Timezone"
                      options={timezoneOptions}
                      value={formData.timezone}
                      onChange={(value) => handleInputChange('timezone', value)}
                      placeholder="Select timezone"
                      disabled={isLoading}
                      searchable
                    />
                    
                    <Select
                      label="Language"
                      options={languageOptions}
                      value={formData.language}
                      onChange={(value) => handleInputChange('language', value)}
                      placeholder="Select language"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Domain Configuration Section */}
                <div className="space-y-5">
                  <div className="flex items-center space-x-2 text-primary pb-3 border-b border-border/60">
                    <Icon name="Shield" size={18} className="text-primary/80" />
                    <h3 className="text-base font-semibold text-foreground">Security & Access</h3>
                  </div>
                  
                  <Input
                    label="Allowed Email Domains"
                    type="text"
                    value={formData.allowed_domains}
                    onChange={(e) => handleInputChange('allowed_domains', e.target.value)}
                    placeholder="example.com, company.org"
                    disabled={isLoading}
                    icon="Lock"
                  />
                  
                  <div className="bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-lg p-4 flex items-start space-x-3">
                    <div className="p-1 bg-blue-100/50 dark:bg-blue-500/20 rounded-full flex-shrink-0 mt-0.5">
                      <Icon name="Info" size={14} className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="text-sm">
                      <p className="font-medium text-blue-900 dark:text-blue-300 mb-1">Domain Restrictions</p>
                      <p className="text-blue-700 dark:text-blue-400 leading-relaxed text-xs">
                        Limit organization membership to specific email domains. Useful for company internal workspaces.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions - Fixed */}
          <div className="flex-shrink-0 flex items-center justify-between p-6 border-t border-border/60 bg-muted/20 rounded-b-xl backdrop-blur-sm">
            <div className="flex items-center space-x-3">
              {currentStep === 2 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePreviousStep}
                  disabled={isLoading}
                  iconName="ChevronLeft"
                  iconPosition="left"
                  className="transition-all hover:bg-background hover:shadow-sm"
                >
                  Back
                </Button>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                type="button"
                variant="ghost"
                onClick={handleClose}
                disabled={isLoading}
                className="hover:bg-destructive/10 hover:text-destructive transition-colors"
              >
                Cancel
              </Button>
              
              <Button
                type="submit"
                variant="default"
                disabled={isLoading || (currentStep === 1 && !formData.name.trim())}
                iconName={isLoading ? "Loader2" : currentStep === 1 ? "ChevronRight" : "CheckCircle2"}
                iconPosition={currentStep === 1 ? "right" : "left"}
                className={`${isLoading ? "animate-spin" : ""} min-w-[130px] shadow-md hover:shadow-lg hover:scale-[1.02] transition-all bg-primary text-primary-foreground`}
              >
                {isLoading ? 'Creating...' : currentStep === 1 ? 'Continue' : 'Create Organization'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrganizationModal;
